"use client";

import {
  Button,
  ButtonSize,
  ButtonVariant,
  Input,
  Text,
  TextColor,
  TextSize,
  TextVariant,
  TextWeight,
} from "@workspace/ui/components";
import type { CustomReturnUserFormValues } from "@workspace/ui/types";
import type { FormikProps } from "formik";
import { Form, Formik } from "formik";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

import validationSchema from "./validationSchema";

export interface CustomReturnUserFormRef {
  isValid: () => boolean;
  getValues: () => CustomReturnUserFormValues;
  validateForm: () => Promise<boolean>;
}

type CustomReturnUserFormProps = {
  onValidationChange?: (isValid: boolean) => void;
  initialData?: CustomReturnUserFormValues | null;
  onDataChange?: (data: CustomReturnUserFormValues) => void;
};

const CustomReturnUserForm = forwardRef<
  CustomReturnUserFormRef,
  CustomReturnUserFormProps
>(({ onValidationChange, initialData, onDataChange }, ref) => {
  const [status, setStatus] = useState({
    unauthorized: false,
    success: false,
  });

  const formRef = useRef<FormikProps<CustomReturnUserFormValues>>(null);

  // InitialValues
  const initialValues: CustomReturnUserFormValues = initialData || {
    accountNumber: "",
    name: "",
    phone: "",
    street: "",
    houseNumber: "",
    apartmentNumber: "",
    postCode: "",
    city: "",
  };

  const formSchema = validationSchema();

  const statusContent: { [key: string]: string } = {
    unauthorized: "Dane są nieprawidłowe",
    success: "Dane są prawidłowe",
  };

  // Expose form methods via ref
  useImperativeHandle(ref, () => ({
    isValid: () => {
      if (!formRef.current) return false;
      const { errors, touched, values } = formRef.current;

      // Sprawdź czy wszystkie wymagane pola są wypełnione i bez błędów
      const requiredFields = [
        "accountNumber",
        "name",
        "phone",
        "street",
        "houseNumber",
        "postCode",
        "city",
      ];

      return requiredFields.every((field) => {
        return (
          touched[field as keyof typeof touched] &&
          !errors[field as keyof typeof errors] &&
          values[field as keyof typeof values]
        );
      });
    },
    getValues: () => formRef.current?.values || initialValues,
    validateForm: async () => {
      if (!formRef.current) return false;
      const errors = await formRef.current.validateForm();
      return Object.keys(errors).length === 0;
    },
  }));

  const handleSubmit = async ({ setSubmitting }: any) => {
    try {
      setStatus({ unauthorized: false, success: true });
    } catch (error) {
      console.error("Form submission error:", error);
      setStatus({ unauthorized: true, success: false });
    } finally {
      setSubmitting(false);
    }
  };

  const formatAccountNumber = (value: string): string => {
    const cleanValue = value.replace(/[^\d]/g, "").substring(0, 26);

    return cleanValue
      .replace(/^(\d{2})/, "$1 ")
      .replace(/(\d{4})(?=\d)/g, "$1 ")
      .trim();
  };

  const formatPostCode = (value: string): string => {
    const cleanValue = value.replace(/[^\d]/g, "");
    if (cleanValue.length <= 2) return cleanValue;
    return `${cleanValue.slice(0, 2)}-${cleanValue.slice(2, 5)}`;
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={formSchema}
      onSubmit={handleSubmit}
      innerRef={formRef}
      enableReinitialize={true}
    >
      {({
        values,
        touched,
        errors,
        setFieldValue,
        isSubmitting,
        setFieldTouched,
      }) => {
        // Effect to notify parent about validation changes
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          if (onValidationChange) {
            const requiredFields = [
              "accountNumber",
              "name",
              "phone",
              "street",
              "houseNumber",
              "postCode",
              "city",
            ];
            const isValid = requiredFields.every((field) => {
              return (
                touched[field as keyof typeof touched] &&
                !errors[field as keyof typeof errors] &&
                values[field as keyof typeof values]
              );
            });
            onValidationChange(isValid);
          }
        }, [values, errors, touched, onValidationChange]);

        // Effect to notify parent about data changes
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          if (onDataChange) {
            onDataChange(values);
          }
        }, [values, onDataChange]);

        return (
          <>
            <FormWrapper className="">
              <Text
                text="Uzupełnij dane do zwrotu"
                weight={TextWeight.BOLD}
                fontSize={TextSize.M}
              />

              <div className="grid grid-cols-1 gap-4 mt-4">
                <Input
                  id="account_number"
                  value={values.accountNumber}
                  name="accountNumber"
                  placeholder="Numer konta bankowego *"
                  focused={touched.accountNumber}
                  validated={
                    typeof errors.accountNumber === "undefined" &&
                    touched.accountNumber
                  }
                  error={
                    typeof errors.accountNumber !== "undefined" &&
                    touched.accountNumber
                  }
                  errorHelper={errors.accountNumber}
                  onChange={(e) => {
                    const formatted = formatAccountNumber(e.target.value);
                    setFieldValue("accountNumber", formatted);
                  }}
                  onBlur={() => setFieldTouched("accountNumber", true)}
                  icon={false}
                  type="text"
                  autoComplete="off"
                />

                <Input
                  id="name"
                  value={values.name}
                  name="name"
                  placeholder="Imię i nazwisko *"
                  focused={touched.name}
                  validated={typeof errors.name === "undefined" && touched.name}
                  error={typeof errors.name !== "undefined" && touched.name}
                  errorHelper={errors.name}
                  onChange={(e) => setFieldValue("name", e.target.value)}
                  onBlur={() => setFieldTouched("name", true)}
                  icon={false}
                  type="text"
                  autoComplete="off"
                />

                <Input
                  id="phone"
                  value={values.phone}
                  name="phone"
                  placeholder="Numer telefonu *"
                  focused={touched.phone}
                  validated={
                    typeof errors.phone === "undefined" && touched.phone
                  }
                  error={typeof errors.phone !== "undefined" && touched.phone}
                  errorHelper={errors.phone}
                  onChange={(e) => setFieldValue("phone", e.target.value)}
                  onBlur={() => setFieldTouched("phone", true)}
                  icon={false}
                  type="text"
                  autoComplete="off"
                />

                <div className="grid grid-cols-4 gap-4">
                  <div className="col-span-2">
                    <Input
                      id="street"
                      value={values.street}
                      name="street"
                      placeholder="Ulica *"
                      focused={touched.street}
                      validated={
                        typeof errors.street === "undefined" && touched.street
                      }
                      error={
                        typeof errors.street !== "undefined" && touched.street
                      }
                      errorHelper={errors.street}
                      onChange={(e) => setFieldValue("street", e.target.value)}
                      onBlur={() => setFieldTouched("street", true)}
                      icon={false}
                      type="text"
                      autoComplete="off"
                    />
                  </div>

                  <div className="col-span-1">
                    <Input
                      id="house_number"
                      value={values.houseNumber}
                      name="houseNumber"
                      placeholder="Nr budynku *"
                      focused={touched.houseNumber}
                      validated={
                        typeof errors.houseNumber === "undefined" &&
                        touched.houseNumber
                      }
                      error={
                        typeof errors.houseNumber !== "undefined" &&
                        touched.houseNumber
                      }
                      errorHelper={errors.houseNumber}
                      onChange={(e) =>
                        setFieldValue("houseNumber", e.target.value)
                      }
                      onBlur={() => setFieldTouched("houseNumber", true)}
                      icon={false}
                      type="text"
                      autoComplete="off"
                    />
                  </div>

                  <div className="col-span-1">
                    <Input
                      id="apartmentNumber"
                      value={values.apartmentNumber}
                      name="apartmentNumber"
                      placeholder="Nr mieszkania"
                      focused={touched.apartmentNumber}
                      validated={
                        typeof errors.apartmentNumber === "undefined" &&
                        touched.apartmentNumber
                      }
                      error={
                        typeof errors.apartmentNumber !== "undefined" &&
                        touched.apartmentNumber
                      }
                      errorHelper={errors.apartmentNumber}
                      onChange={(e) =>
                        setFieldValue("apartmentNumber", e.target.value)
                      }
                      onBlur={() => setFieldTouched("apartmentNumber", true)}
                      icon={false}
                      type="text"
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    id="post_code"
                    value={values.postCode}
                    name="postCode"
                    placeholder="Kod pocztowy *"
                    focused={touched.postCode}
                    validated={
                      typeof errors.postCode === "undefined" && touched.postCode
                    }
                    error={
                      typeof errors.postCode !== "undefined" && touched.postCode
                    }
                    errorHelper={errors.postCode}
                    onChange={(e) => {
                      const formatted = formatPostCode(e.target.value);
                      setFieldValue("postCode", formatted);
                    }}
                    onBlur={() => setFieldTouched("postCode", true)}
                    icon={false}
                    type="text"
                    autoComplete="off"
                    maxLength={6}
                  />

                  <Input
                    id="city"
                    value={values.city}
                    name="city"
                    placeholder="Miejscowość *"
                    focused={touched.city}
                    validated={
                      typeof errors.city === "undefined" && touched.city
                    }
                    error={typeof errors.city !== "undefined" && touched.city}
                    errorHelper={errors.city}
                    onChange={(e) => setFieldValue("city", e.target.value)}
                    onBlur={() => setFieldTouched("city", true)}
                    icon={false}
                    type="text"
                    autoComplete="off"
                  />
                </div>
              </div>

              {Object.values(status).indexOf(true) > -1 && (
                <div
                  className={`flex justify-center items-center mt-4 py-1 ${
                    status.success
                      ? "bg-green-50 text-green-200 border border-green-200"
                      : "bg-red-50 text-red-200 border border-red-200"
                  }`}
                >
                  <Text
                    variant={TextVariant.P}
                    text={
                      Object.values(statusContent)[
                        Object.values(status).indexOf(true)
                      ]
                    }
                    fontSize={TextSize.S}
                    color={
                      status.success
                        ? TextColor.SPECIAL_700
                        : TextColor.ERROR_500
                    }
                  />
                </div>
              )}

              <ButtonsWrapper>
                <Button
                  type="submit"
                  variant={ButtonVariant.SECONDARY}
                  size={ButtonSize.XS}
                  text={isSubmitting ? "Wysyłanie..." : "Sprawdź poprawność"}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                />
              </ButtonsWrapper>
            </FormWrapper>
          </>
        );
      }}
    </Formik>
  );
});

CustomReturnUserForm.displayName = "CustomReturnUserForm";

const FormWrapper = styled(Form)`
  display: flex;
  flex-direction: column;

  button {
    width: 100%;
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 24px;

  button {
    width: fit-content;
    padding: 8px 32px;
  }

  ${({ theme }) => theme.mediaQueries.sm} {
    flex-direction: column-reverse;
    justify-content: center;
  }
`;

export default CustomReturnUserForm;
